import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Matter } from '@/lib/conflix';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { LoadingSpinner } from './ui/loading-spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { checkHasProFeature } from "@/lib/utils";

const apiUrl = process.env.REACT_APP_API_URL;

function RecentMatters({ idToken }: { idToken: string; }) {
  const [matters, setMatters] = useState<Matter[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [hasPro, setHasPro] = useState<boolean>(false);

  useEffect(() => {
    const fetchMatters = async () => {
      try {
        setLoading(true);
        const checkProAccess = async () => {
          if (!apiUrl) return;
          const hasAccess = await checkHasProFeature(apiUrl, idToken);
          setHasPro(hasAccess);
        };

        await checkProAccess();

        // Fetch recent matters
        const response = await axios.get(
          `${apiUrl}matters/recent`,
          {
            headers: {
              "Authorization": idToken
            }
          }
        );

        if (Array.isArray(response.data)) {
          // Sort by creation date (newest first)
          const sortedMatters = [...response.data].sort((a, b) => 
            new Date(b.lastAccessTime).getTime() - new Date(a.lastAccessTime).getTime()
          );
          
          // Take only the 10 most recent matters
          setMatters(sortedMatters.slice(0, 10));
        } else {
          console.warn('Expected array of matters, but received:', response.data);
          setMatters([]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching matters:', error);
        setError('Failed to load recent matters');
        
      }
    };

    

    fetchMatters();
    
  }, [idToken]);

  const navigateToMatterDetail = (matterId: string) => {
    window.history.pushState({}, '', `/matter/${matterId}`);
    // Trigger a popstate event to update the view in App.tsx
    window.dispatchEvent(new Event('popstate'));
  };

  const handleBack = () => {
    window.history.pushState({}, '', '/');
    // Trigger a popstate event to update the view in App.tsx
    window.dispatchEvent(new Event('popstate'));
  };

  if (loading) {
    return (
      <div className="container mx-auto grid justify-items-center">
        <LoadingSpinner size={150} />
        <div>Loading recent matters...</div>
      </div>
    );
  }

  if (!hasPro) {
    return (
      <Card className="max-w-2xl mx-auto my-8">
        <CardHeader>
          <CardTitle>Pro Subscription Required</CardTitle>
          <CardDescription className="text-amber-700">
            Access to recent matters requires a Pro subscription. Please upgrade your account to access this feature.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleBack}>Back to Home</Button>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="max-w-2xl mx-auto my-8">
        <CardHeader>
          <CardTitle>Error</CardTitle>
          <CardDescription className="text-red-600">{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleBack}>Back to Home</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto my-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Recent Matters</CardTitle>
          <CardDescription>Matters you've recently interacted with</CardDescription>
        </CardHeader>
        <CardContent>
          {matters.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No matters found.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference Number</TableHead>
                  <TableHead>Name</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((matter) => (
                  <TableRow 
                    key={matter.matterId}
                    className="cursor-pointer hover:bg-gray-100"
                    onClick={() => hasPro ? navigateToMatterDetail(matter.matterId) : null}
                  >
                    <TableCell>{matter.referenceNumber}</TableCell>
                    <TableCell>{matter.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <div className="flex justify-start">
        <Button onClick={handleBack}>Back to Home</Button>
      </div>
    </div>
  );
}

export default RecentMatters;
